import { z } from "zod";

/*
 * Instance settings registry shared by server and UI: every known key with its Zod schema,
 * default and secret flag. Unknown keys are rejected by the API (`setting_unknown`).
 */

export const MAX_API_KEY_LENGTH = 200;

/** Library languages with fewer books than this are not part of the "automatic" suggestion languages. */
export const AUTOMATIC_LANGUAGE_MIN_BOOKS = 3;
/** Most languages one list (instance setting or personal preference) may hold. */
export const MAX_SUGGESTION_LANGUAGES = 20;

/** Lowercase BCP 47 primary language tag of 2 or 3 letters (`de`, `en`, `haw`). */
export const languageTagSchema = z
  .string({ error: "invalid" })
  .regex(/^[a-z]{2,3}$/, { error: "invalid" });

/** ISO 639 codes that mean "no language" (undetermined, multiple, uncoded, no linguistic content). */
export const NOT_A_LANGUAGE: ReadonlySet<string> = new Set(["und", "mul", "mis", "zxx"]);

/** 1 to 20 distinct language tags; shared by the instance setting and the personal preference. */
export const languageListSchema = z
  .array(
    languageTagSchema.refine((tag) => !NOT_A_LANGUAGE.has(tag), { error: "invalid" }),
    { error: "invalid" },
  )
  .min(1, { error: "required" })
  .max(MAX_SUGGESTION_LANGUAGES, { error: "too_many" })
  .refine((list) => new Set(list).size === list.length, { error: "duplicate" });

export const SETTINGS_REGISTRY = {
  "metadata.lookupsEnabled": {
    schema: z.boolean({ error: "invalid" }),
    default: true as boolean,
    secret: false,
  },
  "metadata.openLibrary.enabled": {
    schema: z.boolean({ error: "invalid" }),
    default: true as boolean,
    secret: false,
  },
  "metadata.googleBooks.enabled": {
    schema: z.boolean({ error: "invalid" }),
    default: true as boolean,
    secret: false,
  },
  "metadata.googleBooks.apiKey": {
    schema: z
      .string({ error: "invalid" })
      .trim()
      .min(1, { error: "required" })
      .max(MAX_API_KEY_LENGTH, { error: "too_long" })
      .nullable(),
    default: null as string | null,
    secret: true,
  },
  "metadata.dnb.enabled": {
    schema: z.boolean({ error: "invalid" }),
    default: true as boolean,
    secret: false,
  },
  "authorSuggestions.enabled": {
    schema: z.boolean({ error: "invalid" }),
    default: true as boolean,
    secret: false,
  },
  /** `null`: automatic, the library's languages with at least {@link AUTOMATIC_LANGUAGE_MIN_BOOKS} books. */
  "authorSuggestions.languages": {
    schema: languageListSchema.nullable(),
    default: null as string[] | null,
    secret: false,
  },
  "authorSuggestions.includeUnknownLanguage": {
    schema: z.boolean({ error: "invalid" }),
    default: false as boolean,
    secret: false,
  },
} as const;

export type SettingKey = keyof typeof SETTINGS_REGISTRY;
export type SettingValue<K extends SettingKey> = z.infer<(typeof SETTINGS_REGISTRY)[K]["schema"]>;

export const SETTING_KEYS = Object.keys(SETTINGS_REGISTRY) as SettingKey[];

export function isSettingKey(key: string): key is SettingKey {
  return Object.hasOwn(SETTINGS_REGISTRY, key);
}

/** One setting as returned by `GET /api/admin/settings`; secrets never carry their value. */
export interface SettingDto {
  key: SettingKey;
  secret: boolean;
  /** Effective value; omitted for secrets. Booleans, except `authorSuggestions.languages` (list or null = automatic). */
  value?: boolean | string[] | null;
  /** Secrets only: whether a value is stored and its last 4 characters. */
  configured?: boolean;
  last4?: string | null;
  isDefault: boolean;
  /** ISO timestamp of the last change; `null` while the default applies. */
  updatedAt: string | null;
}

/** A non-fatal note returned with a successful update (for example an unverified Google key). */
export interface SettingWarningDto {
  key: SettingKey;
  code: "google_api_key_unverified" | "google_api_key_quota_exceeded";
  /** `google_api_key_unverified` only. */
  reason?: "unreachable" | "lookups_disabled";
}

/** Languages for the admin's choice: counts of library books and of cached suggestion editions. */
export interface SuggestionLanguagesContextDto {
  /** The languages that apply now: the list, or the automatic result (>= 3 books each, else `de`, `en`) ordered by count. */
  resolved: { code: string; books: number }[];
  /** Library languages left out of the automatic result (fewer than 3 books); empty when a list is chosen. */
  belowThreshold: { code: string; books: number }[];
  /** Languages found among the cached suggestion editions (`und` for unknown), ordered by count. */
  cached: { code: string; editions: number }[];
}

export interface SettingsDto {
  settings: SettingDto[];
  suggestionLanguages: SuggestionLanguagesContextDto;
  /** Empty when there is nothing to report (always empty for GET). */
  warnings: SettingWarningDto[];
}

export const METADATA_PROVIDER_IDS = ["openlibrary", "googlebooks", "dnb"] as const;
export type MetadataProviderId = (typeof METADATA_PROVIDER_IDS)[number];

/** Response of `GET /api/settings/features` (non-secret, derived flags for the UI). */
export interface FeaturesDto {
  metadataLookup: { enabled: boolean; providers: MetadataProviderId[] };
  /** Outgoing email (SMTP) is configured; enables Send to Kindle. */
  email: { configured: boolean };
  /** `refreshAvailable`: enabled, master switch on, and Open Library or the DNB enabled. */
  authorSuggestions: { enabled: boolean; refreshAvailable: boolean };
}

/** Hosts contacted when metadata lookups are enabled (shown in the admin UI and the README). */
export const METADATA_HOSTS = [
  "openlibrary.org",
  "covers.openlibrary.org",
  "archive.org",
  "www.googleapis.com",
  "books.google.com",
  "books.googleusercontent.com",
  "services.dnb.de",
  "portal.dnb.de",
] as const;
